import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Input,
  Select,
  Spinner,
  Text,
  Textarea,
  useToast,
  FormControl,
  FormLabel,
  Divider,
  HStack,
  useRadioGroup,
  useRadio,
  chakra,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import axios from "axios";
import moment from "moment";
import InputSuggestionWithExposeRef from "../../components/InputSuggestionWithExposeRef";
import RouteFlow from "../../components/RouteFlow";
import { usePaymentLinkExpiry } from "../../hooks/usePaymentLinkExpiry";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in";

const EditLead = () => {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [estimatedFare, setEstimatedFare] = useState(0);
  const [baseFare, setBaseFare] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [tokenAmount, setTokenAmount] = useState(0);
  const [hours, setHours] = useState(null);
  const [airportTripType, setAirportTripType] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentLinkSentTime, setPaymentLinkSentTime] = useState(null);
  const [paymentLinkExpiresAt, setPaymentLinkExpiresAt] = useState(null);
  const { isExpired, formattedTime, canResend, expiresAt } =
    usePaymentLinkExpiry(paymentLinkSentTime, paymentLinkExpiresAt);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [paymentId, setPaymentId] = useState(null);

  const [paymentStatus, setPaymentStatus] = useState("PENDING");
  const navigate = useNavigate();
  const toast = useToast();

  // Refs for location inputs
  const fromRef = useRef();
  const toRef = useRef();

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const token = localStorage.getItem("prima_session");
        if (!token) {
          toast({
            title: "Authentication required",
            status: "error",
            duration: 3000,
          });
          navigate("/bookings");
          return;
        }

        const res = await axios.get(`${SERVER}/management/lead?id=${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const leadData = res.data.data;
        // console.log("Fetched lead data:", res);
        // Initialize estimatedFare from fareDetails.total if not already set
        if (
          leadData.estimatedFare === undefined &&
          leadData.fareDetails?.total
        ) {
          leadData.estimatedFare = leadData.fareDetails.total;
        }
        setLead(leadData);
        setEstimatedFare(leadData.estimatedFare || 0);
        setBaseFare(
          leadData.fareDetails?.baseFare || leadData.estimatedFare || 0,
        );
        setDiscount(leadData.fareDetails?.discounts || 0);

        // Calculate token amount as 15% of estimated fare
        const calculatedTokenAmount = Math.round(
          (leadData.fareDetails?.total || leadData.estimatedFare || 0) * 0.15,
        );
        setTokenAmount(calculatedTokenAmount);

        // Fetch payment details for this lead (regardless of status)
        try {
          const paymentRes = await axios.get(
            `${SERVER}/management/payment-by-lead/${leadData.leadID}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (paymentRes.data?.data) {
            const payment = paymentRes.data.data;
            setPaymentId(payment._id);
            setPaymentStatus(payment.status || "Pending");
            if (payment.createdAt) {
              setPaymentLinkSentTime(new Date(payment.createdAt));
            }
            if (payment.expiresAt) {
              setPaymentLinkExpiresAt(new Date(payment.expiresAt));
            }
            // Use calculated token amount (15%) instead of stored payment amount
            // This ensures we always show the current 15% calculation
            setTokenAmount(calculatedTokenAmount);
          }
        } catch (paymentErr) {
          console.log("No payment found for this lead");
        }

        // Initialize hours for local rental from tripDetails
        if (leadData.type === "localRental" && leadData.tripDetails?.hours) {
          setHours(leadData.tripDetails.hours);
        }
      } catch (err) {
        console.error(err);
        toast({
          title: "Error loading lead",
          description:
            err.response?.data?.message || "Failed to fetch lead details",
          status: "error",
          duration: 4000,
        });
        navigate("/bookings");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchLead();
    }
  }, [id, navigate, toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem("prima_session");

      // Build tripDetails object
      const tripDetails = lead.tripDetails || {};
      if (lead.type === "localRental" && hours) {
        tripDetails.hours = hours;
      }

      await axios.patch(
        `${SERVER}/management/lead/${id}`,
        {
          type: lead.type,
          fromLocation: lead.fromLocation,
          toLocation: lead.toLocation,
          pickupDateTime: lead.pickupDateTime,
          estimatedFare: estimatedFare,
          fareDetails: {
            baseFare: baseFare,
            discounts: discount,
            total: estimatedFare,
          },
          tripDetails: tripDetails,
          hours: hours,
          airportTripType: airportTripType,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast({
        title: "Lead updated successfully",
        status: "success",
        duration: 3000,
      });

      navigate(`/leads/${id}`);
    } catch (err) {
      console.error(err);
      toast({
        title: "Error updating lead",
        description: err.response?.data?.message || "Failed to update lead",
        status: "error",
        duration: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConvertToBooking = async () => {
    if (
      !window.confirm(
        `Are you sure you want to convert lead ${lead.leadID} to a booking?`,
      )
    ) {
      return;
    }

    setConverting(true);

    try {
      const token = localStorage.getItem("prima_session");

      const response = await axios.post(
        `${SERVER}/management/lead-to-booking`,
        { leadId: lead.leadID },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast({
        title: "Success!",
        description: `Lead ${lead.leadID} converted to booking successfully`,
        status: "success",
        duration: 3000,
      });

      // Navigate to bookings tab
      navigate("/bookings");
    } catch (err) {
      console.error("Convert to booking error:", err);
      toast({
        title: "Conversion Failed",
        description:
          err.response?.data?.message || "Failed to convert lead to booking",
        status: "error",
        duration: 4000,
      });
    } finally {
      setConverting(false);
    }
  };

  // --------------------------------------------
  // MARK AS PAID
  // --------------------------------------------
  const handleMarkAsPaid = async () => {
    setPaymentLoading(true);

    try {
      const token = localStorage.getItem("prima_session");

      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Session token not found. Please login again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setPaymentLoading(false);
        return;
      }

      const transactionID = `TXN${Date.now()}`;

      if (!paymentId) {
        toast({
          title: "Error",
          description:
            "Payment ID is missing. Please convert lead to booking first.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setPaymentLoading(false);
        return;
      }

      if (!lead.leadID) {
        toast({
          title: "Error",
          description: "Lead ID is missing.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setPaymentLoading(false);
        return;
      }

      if (!tokenAmount || isNaN(tokenAmount) || tokenAmount <= 0) {
        toast({
          title: "Invalid Amount",
          description: "Token amount is missing or invalid.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setPaymentLoading(false);
        return;
      }

      let res;
      // If a receipt file is attached, send multipart/form-data
      if (receiptFile) {
        const form = new FormData();
        form.append("leadId", lead._id);
        form.append("transactionID", transactionID);
        form.append("amount", Number(tokenAmount));
        form.append("manualOverride", true);
        form.append("receipt", receiptFile);

        res = await axios.patch(
          `${SERVER}/management/payment-paid/${paymentId}`,
          form,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } else {
        res = await axios.patch(
          `${SERVER}/management/payment-paid/${paymentId}`,
          {
            leadId: lead._id,
            transactionID,
            amount: Number(tokenAmount),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      toast({
        title: "Success!",
        description: "Payment marked as paid and lead converted to booking.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setPaymentStatus("Paid");

      // clear receipt after success
      setReceiptFile(null);
      setReceiptPreview(null);

      // Backend already converts lead to booking when marking payment as paid
      // Navigate back to bookings list after a short delay
      setTimeout(() => {
        navigate("/bookings");
      }, 1500);
    } catch (error) {
      console.error("Payment Error:", error);

      let errorMessage = "An error occurred while marking payment as paid";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Error Marking Payment",
        description: errorMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  // --------------------------------------------
  // SEND TOKEN REQUEST
  // --------------------------------------------
  const handleSendToken = async () => {
    try {
      // Check if a payment link is already active
      if (paymentLinkSentTime && !isExpired) {
        const expiryTime = paymentLinkExpiresAt
          ? new Date(paymentLinkExpiresAt)
          : expiresAt ||
            new Date(paymentLinkSentTime.getTime() + 16 * 60 * 1000);
        const formattedExpiry = expiryTime.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        toast({
          title: "Payment Link Already Active",
          description: `A payment link is already active. You can resend after it expires at ${formattedExpiry}. Time remaining: ${formattedTime}`,
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      const token = localStorage.getItem("prima_session");
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Session token not found. Please login again.",
          status: "error",
          duration: 4000,
        });
        return;
      }

      if (!lead?.leadID) {
        toast({
          title: "Error",
          description: "Lead ID is missing.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      if (!estimatedFare || estimatedFare <= 0) {
        toast({
          title: "Invalid Fare",
          description:
            "Please set a valid estimated fare before sending token request.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // Calculate token amount (15% of estimated fare)
      const tokenAmt = Math.round(estimatedFare * 0.15);
      if (tokenAmt < 1) {
        toast({
          title: "Invalid Token Amount",
          description: "Token amount must be at least ₹1.",
          status: "warning",
          duration: 3000,
        });
        return;
      }

      const payload = {
        amount: tokenAmt,
        userPhone: lead.customer?.mobile || "",
        userEmail: lead.customer?.email || "",
        orderId: lead.leadID,
        leadId: lead._id,
        customerId: lead.customer?._id || null,
      };

      setPaymentLoading(true);

      const res = await axios.post(`${SERVER}/payments/send-token`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const paymentId =
        res.data?.paymentLinkId ||
        res.data?.data?._id ||
        res.data?.data?.paymentId ||
        res.data?._id;

      toast({
        title: "Token Request Sent!",
        description: "Payment link has been sent to customer.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Set payment link sent time for expiry tracking
      setPaymentLinkSentTime(new Date());

      // Calculate expiry time (16 minutes from now)
      const expiryTime = new Date(Date.now() + 16 * 60 * 1000);
      setPaymentLinkExpiresAt(expiryTime);
      const formattedExpiry = expiryTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      toast({
        title: "Link Expires Soon",
        description: `Payment link will expire at ${formattedExpiry} (16 minutes from now). You can resend after it expires.`,
        status: "info",
        duration: 5000,
        isClosable: true,
      });

      // Refresh lead data to get updated payment info
      const leadRes = await axios.get(`${SERVER}/management/lead?id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedLead = leadRes.data.data;
      setLead(updatedLead);

      // Fetch the newly created payment
      if (paymentId) {
        try {
          const paymentRes = await axios.get(
            `${SERVER}/management/payment-by-lead/${lead.leadID}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (paymentRes.data?.data) {
            const payment = paymentRes.data.data;
            setPaymentId(payment._id);
            setPaymentStatus(payment.status || "Pending");
            setTokenAmount(payment.amount || tokenAmt);
          }
        } catch (paymentErr) {
          console.log("Error fetching payment:", paymentErr);
        }
      }
      navigate('/bookings');
    } catch (error) {
      console.error("Token Error:", error?.response?.data || error.message);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        "Unknown error occurred";
      toast({
        title: "Error Sending Token",
        description: `Request failed: ${errorMessage}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setLead((prev) => ({ ...prev, [field]: value }));
  };

  // Handle location selection from search suggestions
  const handleLocationSelect = (location, type) => {
    if (!location || typeof location !== "string") return;

    // Save to recent searches
    const storageKey = type === "from" ? "recentFromCities" : "recentToCities";
    let recent = [];

    try {
      const stored = localStorage.getItem(storageKey);
      recent = stored ? JSON.parse(stored) : [];
    } catch (e) {
      recent = [];
    }

    // Remove if already exists to avoid duplicates
    recent = recent.filter((item) => item !== location);

    // Add to beginning
    recent.unshift(location);

    // Keep only last 5 searches
    recent = recent.slice(0, 5);

    localStorage.setItem(storageKey, JSON.stringify(recent));
  };

  // Radio custom component (same as Form.jsx)
  const CustomRadio = ({ name, radioProps, isDisabled }) => {
    const { state, getInputProps, htmlProps } = useRadio(radioProps);
    return (
      <chakra.label
        {...htmlProps}
        cursor={isDisabled ? "not-allowed" : "pointer"}
        opacity={isDisabled ? 0.6 : 1}
        display="flex"
        alignItems="center"
        borderRadius="50"
        p={2}
      >
        <input {...getInputProps()} disabled={isDisabled} hidden />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="24px"
          h="24px"
          mr={2}
        >
          <Box
            border="2px solid"
            borderColor={state.isChecked ? "#1814F3" : "#CBD5E0"}
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="100%"
            h="100%"
            bg="white"
            p="3.1px"
            boxSizing="border-box"
          >
            {state.isChecked && (
              <Box
                w="16px"
                h="16px"
                bg="#1814F3"
                borderRadius="full"
                boxShadow="0 0 15px #1814F3"
              />
            )}
          </Box>
        </Box>
        <Box
          fontSize="sm"
          fontWeight="500"
          color={state.isChecked ? "#1814F3" : "#000914"}
        >
          {name}
        </Box>
      </chakra.label>
    );
  };

  const normalizeType = (type) => {
    if (!type) return "outstationOneWay";
    switch (type.toUpperCase()) {
      case "ONEWAY":
      case "ONE WAY":
      case "OW":
      case "OUTSTATIONONEWAY":
        return "outstationOneWay";
      case "ROUNDTRIP":
      case "ROUND TRIP":
      case "RT":
      case "OUTSTATIONROUNDTRIP":
        return "outstationRoundTrip";
      case "LOCAL":
      case "LOCALRENTAL":
        return "localRental";
      case "AIRPORT":
      case "AT":
      case "AIRPORTTRANSFER":
        return "airportTransfer";
      default:
        return "outstationOneWay";
    }
  };

  const radioOptions = [
    { name: "One Way", value: "outstationOneWay" },
    { name: "Round Trip", value: "outstationRoundTrip" },
    { name: "Local", value: "localRental" },
    { name: "Airport", value: "airportTransfer" },
  ];

  const [selectedTripType, setSelectedTripType] = useState("OW");
  const [originalToLocation, setOriginalToLocation] = useState(null);

  const { getRadioProps, getRootProps } = useRadioGroup({
    value: selectedTripType,
    name: "tripType",
    isDisabled: true,
    onChange: (val) => {
      // Store the original toLocation before changing to Local
      if (
        val === "localRental" &&
        lead?.toLocation &&
        lead.toLocation !== lead.fromLocation
      ) {
        setOriginalToLocation(lead.toLocation);
      }

      // Restore original toLocation when switching away from Local
      if (
        selectedTripType === "localRental" &&
        val !== "localRental" &&
        originalToLocation
      ) {
        setLead((prev) => ({ ...prev, toLocation: originalToLocation }));
        setOriginalToLocation(null);
      }

      setSelectedTripType(val);
      handleChange("type", val);
    },
  });

  // Update selectedTripType when lead data loads
  useEffect(() => {
    if (lead?.type) {
      setSelectedTripType(normalizeType(lead.type));
    }
  }, [lead?.type]);

  // Manual fare calculation function (called onBlur)
  const calculateFare = async () => {
    if (!lead?.type || !lead?.pickupDateTime) {
      console.log("Missing required fields for fare calculation:", {
        type: !!lead?.type,
        pickupDateTime: !!lead?.pickupDateTime,
      });
      return;
    }

    // For non-local rental trips, require both from and to locations
    if (lead.type !== "localRental") {
      if (!lead?.fromLocation || !lead?.toLocation) {
        console.log("Missing required fields for fare calculation:", {
          fromLocation: !!lead?.fromLocation,
          toLocation: !!lead?.toLocation,
        });
        return;
      }
    } else {
      // For local rental, we need hours
      if (!hours) {
        console.log("Hours not set for local rental");
        return;
      }
    }

    // Check if vehicleSegment exists
    if (!lead?.vehicleSegment) {
      console.log("No vehicleSegment found");
      return;
    }

    try {
      const token = localStorage.getItem("prima_session");
      if (!token) {
        toast({
          title: "Authentication required",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // Prepare payload for fare calculation
      const pickupDate = lead.pickupDateTime
        ? moment(lead.pickupDateTime).format("YYYY-MM-DD")
        : null;
      const pickupTime = lead.pickupDateTime
        ? moment(lead.pickupDateTime).format("HH:mm")
        : null;

      let payload;

      if (lead.type === "localRental") {
        // For local rental, get includedKm from vehicle segment pricing based on selected hours
        let includedKm = 40; // Default fallback

        if (lead.vehicleSegment?.pricing?.localRental && hours) {
          const matchingPackage = lead.vehicleSegment.pricing.localRental.find(
            (pkg) => pkg.durationHours === hours,
          );
          if (matchingPackage) {
            includedKm = matchingPackage.includedKm;
          }
        }

        payload = {
          from: lead.fromLocation || "Local",
          to: lead.toLocation || lead.fromLocation || "Local",
          tripType: "LR",
          pickupDate: pickupDate,
          pickupTime: pickupTime,
          localRentalTime: hours,
          localRentalDistance: includedKm,
        };
      } else {
        // For other trip types, calculate distance first
        const distanceRes = await axios.post(
          `${SERVER}/management/calculate-distance`,
          {
            from: lead.fromLocation,
            to: lead.toLocation,
            tripType: lead.type,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const distance = distanceRes.data.data.distance;

        payload = {
          from: lead.fromLocation,
          to: lead.toLocation,
          tripType: lead.type,
          pickupDate: pickupDate,
          pickupTime: pickupTime,
          distance: distance,
        };
      }

      // Calculate fare using /management/find endpoint
      const fareRes = await axios.post(`${SERVER}/management/find`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // The response is an array of fares, find the one matching the current vehicle segment
      const fares = fareRes.data.data;
      if (fares && fares.length > 0) {
        // Find fare for current vehicle segment
        const matchingFare = fares.find(
          (f) => f.id === lead.vehicleSegment._id,
        );
        const fare = matchingFare
          ? matchingFare.customerPayable
          : fares[0].customerPayable;

        // Update base fare to the calculated fare
        setBaseFare(fare);
        // Calculate estimated fare after discount
        const newEstimatedFare = fare - discount;
        setEstimatedFare(newEstimatedFare);
        setLead((prev) => ({ ...prev, estimatedFare: newEstimatedFare }));

        if (newEstimatedFare > 0) {
          const newTokenAmount = Math.round(newEstimatedFare * 0.15);
          setTokenAmount(newTokenAmount);
        }

        toast({
          title: "Fare calculated",
          description: `Base fare: ₹${fare}, Estimated fare: ₹${newEstimatedFare}`,
          status: "success",
          duration: 2000,
        });
      }
    } catch (err) {
      console.error("Fare calculation error:", err);
      console.error("Error details:", {
        message: err.response?.data?.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      toast({
        title: "Fare calculation failed",
        description: err.response?.data?.message || "Could not calculate fare",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Update form fields based on trip type changes
  useEffect(() => {
    if (!lead?.type) return;

    setLead((prev) => {
      const updated = { ...prev };

      switch (prev.type) {
        case "localRental":
          // For local trips, set toLocation to fromLocation only if toLocation is empty
          if (prev.fromLocation && !prev.toLocation) {
            updated.toLocation = prev.fromLocation;
          }
          break;

        case "airportTransfer":
          // For airport trips, ensure both locations are required
          // Could add specific validation here if needed
          setHours(null); // Reset hours for non-local
          if (!airportTripType) {
            setAirportTripType("PFA"); // Default to Pickup From Airport
          }
          break;

        case "outstationOneWay":
        case "outstationRoundTrip":
          // For one-way and round-trip, ensure both locations are set
          // Reset any auto-filled local trip data
          setHours(null); // Reset hours for non-local
          setAirportTripType(""); // Reset airport type
          break;

        default:
          setHours(null);
          setAirportTripType("");
          break;
      }

      return updated;
    });
  }, [lead?.type]);

  // Recalculate fare when hours change for local rental
  useEffect(() => {
    if (
      lead?.type === "localRental" &&
      hours &&
      lead?.vehicleSegment &&
      lead?.pickupDateTime
    ) {
      calculateFare();
    }
  }, [hours]);

  // Update token amount when estimated fare changes (for converted leads with payment)
  useEffect(() => {
    if (paymentId && estimatedFare > 0) {
      // Calculate 15% of estimated fare as token amount
      const newTokenAmount = Math.round(estimatedFare * 0.15);
      setTokenAmount(newTokenAmount);
    }
  }, [estimatedFare, paymentId]);

  if (loading) {
    return <Loading />;
  }

  if (!lead) {
    return <Text p={6}>Lead not found</Text>;
  }

  return (
    <Box px={6} py={8}>
      <BreadCrumbMaker
        items={[
          { link: "/bookings", title: "Leads" },
          { link: `/leads/${id}`, title: "Lead Details" },
          { link: `/bookings/lead/edit/${id}`, title: "Edit Lead", on: true },
        ]}
      />

      <Box bg="white" borderRadius="xl" p={8} mt={4}>
        <Heading size="md" mb={6}>
          Edit Lead - {lead.leadID}
        </Heading>

        <form onSubmit={handleSubmit}>
          {/* Read-only Customer Information */}
          <Box mb={6}>
            <Heading size="sm" mb={4} color="gray.600">
              Customer Information (Read-only)
            </Heading>
            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Customer Name
                </FormLabel>
                <Input
                  value={lead.customer?.name || "-"}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Mobile
                </FormLabel>
                <Input
                  value={lead.customer?.mobile || "-"}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Email
                </FormLabel>
                <Input
                  value={lead.customer?.email || "-"}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>

              {lead.customer?.isCorporate &&
                lead.customer?.corporateDetails && (
                  <>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.500">
                        Customer Type
                      </FormLabel>
                      <Badge colorScheme="purple" mt={2}>
                        Corporate
                      </Badge>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.500">
                        Company Name
                      </FormLabel>
                      <Input
                        value={
                          lead.customer.corporateDetails.companyName || "-"
                        }
                        isReadOnly
                        bg="gray.50"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.500">
                        GSTIN
                      </FormLabel>
                      <Input
                        value={lead.customer.corporateDetails.gstNo || "-"}
                        isReadOnly
                        bg="gray.50"
                      />
                    </FormControl>
                  </>
                )}
            </Grid>
          </Box>

          <Divider my={6} />

          {/* Editable Trip Details */}
          <Heading size="sm" mb={4}>
            Trip Details
          </Heading>

          {/* Trip Type Radio Buttons */}
          <Box mb={6}>
            <FormLabel fontSize="sm" fontWeight="600" mb={3}>
              Trip Type
            </FormLabel>
            <Box {...getRootProps()}>
              <HStack spacing={4} flexWrap="wrap">
                {radioOptions.map((option) => (
                  <CustomRadio
                    key={option.value}
                    name={option.name}
                    radioProps={getRadioProps({ value: option.value })}
                    isDisabled={true}
                  />
                ))}
              </HStack>
            </Box>
          </Box>

          {/* Airport Trip Type Select */}
          {lead.type === "airportTransfer" && (
            <Box mb={6}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">
                  Airport Service Type
                </FormLabel>
                <Select
                  value={airportTripType}
                  onChange={(e) => setAirportTripType(e.target.value)}
                  placeholder="Select airport service type"
                >
                  <option value="PFA">Pickup From Airport</option>
                  <option value="DTA">Drop At Airport</option>
                </Select>
              </FormControl>
            </Box>
          )}

          <Grid templateColumns="repeat(2, 1fr)" gap={6}>
            <GridItem>
              <FormControl isRequired>
                <FormLabel>
                  {lead.type === "airportTransfer" && airportTripType === "PFA"
                    ? "From Location (Airport)"
                    : lead.type === "airportTransfer" &&
                        airportTripType === "DTA"
                      ? "From Location"
                      : lead.type === "localRental"
                        ? "Pickup Location"
                        : "From Location"}
                </FormLabel>
                <InputSuggestionWithExposeRef
                  ref={fromRef}
                  name="fromLocation"
                  placeholder={
                    lead.type === "airportTransfer" && airportTripType === "PFA"
                      ? "Enter Airport"
                      : lead.type === "airportTransfer" &&
                          airportTripType === "DTA"
                        ? "Enter Pickup Location"
                        : lead.type === "localRental"
                          ? "Enter Pickup Location"
                          : "Enter Start Location"
                  }
                  value={lead.fromLocation || ""}
                  onChange={(e) => handleChange("fromLocation", e.target.value)}
                  onSelect={(location) =>
                    handleLocationSelect(location, "from")
                  }
                  onBlur={calculateFare}
                  filter={
                    lead.type === "airportTransfer" && airportTripType === "PFA"
                      ? "airport_with_terminal"
                      : lead.type === "airportTransfer"
                        ? "address"
                        : "address"
                  }
                  country="in"
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isRequired={lead.type !== "LOCAL"}>
                <FormLabel>
                  {lead.type === "airportTransfer" && airportTripType === "PFA"
                    ? "To Location (Hotel/City)"
                    : lead.type === "airportTransfer" &&
                        airportTripType === "DTA"
                      ? "To Location (Airport)"
                      : lead.type === "localRental"
                        ? "To Location (Optional for Local)"
                        : "To Location"}
                </FormLabel>
                <InputSuggestionWithExposeRef
                  ref={toRef}
                  name="toLocation"
                  placeholder={
                    lead.type === "airportTransfer" && airportTripType === "PFA"
                      ? "Enter Hotel or Destination"
                      : lead.type === "airportTransfer" &&
                          airportTripType === "DTA"
                        ? "Enter Airport"
                        : lead.type === "localRental"
                          ? "Enter Drop Location (Optional)"
                          : "Enter Destination"
                  }
                  value={lead.toLocation || ""}
                  onChange={(e) => handleChange("toLocation", e.target.value)}
                  onSelect={(location) => handleLocationSelect(location, "to")}
                  onBlur={calculateFare}
                  filter={
                    lead.type === "airportTransfer" && airportTripType === "DTA"
                      ? "airport_with_terminal"
                      : "address"
                  }
                  country="in"
                  isDisabled={
                    lead.type === "localRental" &&
                    lead.fromLocation &&
                    !lead.toLocation
                  }
                />
                {lead.type === "localRental" && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    For local trips, leave empty or same as pickup location
                  </Text>
                )}
              </FormControl>
            </GridItem>

            {(lead.tripDetails?.stops || []).length > 0 && (
              <GridItem colSpan={2}>
                <RouteFlow
                  from={lead.fromLocation}
                  to={lead.toLocation}
                  stops={lead.tripDetails?.stops || []}
                  tripType={lead.type}
                />
              </GridItem>
            )}

            <GridItem>
              <FormControl isRequired>
                <FormLabel>
                  {lead.type === "localRental"
                    ? "Service Date & Time"
                    : lead.type === "airportTransfer"
                      ? "Flight/Pickup Date & Time"
                      : "Pickup Date & Time"}
                </FormLabel>
                <Input
                  type="datetime-local"
                  value={
                    lead.pickupDateTime
                      ? moment(lead.pickupDateTime).format("YYYY-MM-DDTHH:mm")
                      : ""
                  }
                  onChange={(e) =>
                    handleChange("pickupDateTime", e.target.value)
                  }
                />
                {lead.type === "airportTransfer" && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Please specify flight time for airport transfers
                  </Text>
                )}
                {lead.type === "LOCAL" && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Specify when you need the local service
                  </Text>
                )}
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Base Fare (₹)</FormLabel>
                <Input
                  type="number"
                  value={baseFare}
                  isDisabled
                  onChange={(e) => {
                    const newBaseFare = parseFloat(e.target.value) || 0;
                    setBaseFare(newBaseFare);
                    // Auto-calculate estimated fare
                    setEstimatedFare(newBaseFare - discount);
                  }}
                  placeholder="Enter base fare"
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Discount (₹)</FormLabel>
                <Input
                  type="number"
                  value={discount}
                  isDisabled
                  onChange={(e) => {
                    const newDiscount = parseFloat(e.target.value) || 0;
                    setDiscount(newDiscount);
                    // Auto-calculate estimated fare
                    setEstimatedFare(baseFare - newDiscount);
                  }}
                  placeholder="Enter discount"
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Estimated Fare (₹)</FormLabel>
                <Input
                  type="number"
                  value={estimatedFare}
                  isDisabled
                  placeholder="Auto-calculated"
                  bg="gray.50"
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Token Amount (₹)</FormLabel>
                <Input
                  type="number"
                  value={Math.round(tokenAmount)}
                  isDisabled
                  placeholder="Token amount"
                  bg="gray.50"
                />
              </FormControl>
            </GridItem>

            {lead.type === "localRental" && (
              <GridItem>
                <FormControl>
                  <FormLabel>Hours - included KMs</FormLabel>
                  <Select
                    value={hours || ""}
                    onChange={(e) => setHours(parseInt(e.target.value) || null)}
                    placeholder="Select hours"
                  >
                    {lead.vehicleSegment?.pricing?.localRental?.map((pkg) => (
                      <option key={pkg.durationHours} value={pkg.durationHours}>
                        {pkg.durationHours} Hours - {pkg.includedKm} KM
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
            )}
          </Grid>

          {/* Payment Section - Show if payment exists OR if lead can receive token */}
          {paymentId ? (
            <Box mt={6} p={4} bg="gray.50" borderRadius="md">
              <Heading size="sm" mb={4}>
                Token Payment
              </Heading>
              <HStack spacing={4} align="start">
                <Box>
                  <Text fontSize="sm" color="gray.600">
                    Status
                  </Text>
                  <Badge
                    colorScheme={paymentStatus === "Paid" ? "green" : "orange"}
                  >
                    {paymentStatus || "PENDING"}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600">
                    Token Amount
                  </Text>
                  <Text fontWeight="600">₹ {Math.round(tokenAmount)}</Text>
                </Box>
              </HStack>

              {/* Receipt upload */}
              <Box mt={4}>
                <input
                  id="receiptFile"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (f) {
                      setReceiptFile(f);
                      setReceiptPreview(URL.createObjectURL(f));
                    } else {
                      setReceiptFile(null);
                      setReceiptPreview(null);
                    }
                  }}
                />
                <label htmlFor="receiptFile">
                  <Button mt={2} size="sm" as="span">
                    {receiptFile ? "Change Receipt" : "Manual Payment"}
                  </Button>
                </label>
                {receiptPreview && (
                  <Box mt={2}>
                    <img
                      src={receiptPreview}
                      alt="receipt"
                      style={{ maxWidth: 240, maxHeight: 160 }}
                    />
                    <Button
                      size="xs"
                      ml={2}
                      onClick={() => {
                        setReceiptFile(null);
                        setReceiptPreview(null);
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                )}
              </Box>

              <Button
                mt={4}
                colorScheme="blue"
                size="sm"
                onClick={handleMarkAsPaid}
                isLoading={paymentLoading}
                isDisabled={paymentStatus === "Paid"}
              >
                Mark as Paid
              </Button>
            </Box>
          ) : (
            <Box mt={6} p={4} bg="blue.50" borderRadius="md">
              <Heading size="sm" mb={2}>
                Token Payment
              </Heading>
              <Text fontSize="sm" color="gray.600" mb={4}>
                Send a token payment request to the customer (15% of estimated
                fare: ₹{Math.round(estimatedFare * 0.15)})
              </Text>
              <Flex gap={4} align="center">
                <Button
                  colorScheme="blue"
                  size="sm"
                  onClick={handleSendToken}
                  isLoading={paymentLoading}
                  isDisabled={!estimatedFare || estimatedFare <= 0 }
                >
                  Send Token Request
                </Button>
                {!isExpired && paymentLinkSentTime && (
                  <Text fontSize="sm" fontWeight="600" color="orange.600">
                    Expires in: {formattedTime}
                  </Text>
                )}
              </Flex>
            </Box>
          )}

          <Flex justify="space-between" align="center" gap={4} mt={8}>
            {(lead.status === "Converted" || lead.status === "CONVERTED") && (
              <Text color="green.600" fontWeight="600">
                ✓ This lead has been converted to a booking
              </Text>
            )}
            <Flex gap={4}>
              <Button
                variant="outline"
                onClick={() => navigate("/bookings")}
                isDisabled={submitting || converting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={submitting}
                loadingText="Updating..."
                isDisabled={converting}
              >
                Update Lead
              </Button>
            </Flex>
          </Flex>
        </form>
      </Box>
    </Box>
  );
};

export default EditLead;
